import React, { useState, useEffect, useRef } from 'react';
import { Play, Loader2, AlertCircle } from 'lucide-react';

/**
 * VideoPlayer - Embeds Vimeo, YouTube, Bunny.net Stream, and Rumble videos directly in the app
 * Supports all four platforms with automatic detection
 * 
 * @param {string} url - Video URL (Vimeo, YouTube, Bunny Stream, or Rumble)
 * @param {string} className - Additional CSS classes
 * @param {boolean} autoplay - Whether to autoplay (default: false)
 * @param {boolean} showThumbnail - Show thumbnail with play button instead of immediate embed
 * @param {function} onThumbnailLoad - Callback when thumbnail URL is fetched
 */
const VideoPlayer = ({ 
  url, 
  className = '', 
  autoplay = false,
  showThumbnail = false,
  onThumbnailLoad = null
}) => {
  const [videoData, setVideoData] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playing, setPlaying] = useState(!showThumbnail);
  const iframeRef = useRef(null);

  // Detect video platform and extract ID
  const parseVideoUrl = (videoUrl) => {
    if (!videoUrl) return null;

    // YouTube patterns
    // Standard: https://www.youtube.com/watch?v=VIDEO_ID
    // Short: https://youtu.be/VIDEO_ID
    // Embed: https://www.youtube.com/embed/VIDEO_ID
    // With timestamp: https://www.youtube.com/watch?v=VIDEO_ID&t=123
    const youtubePatterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/
    ];

    for (const pattern of youtubePatterns) {
      const match = videoUrl.match(pattern);
      if (match) {
        return { platform: 'youtube', id: match[1] };
      }
    }

    // Vimeo patterns
    // Standard: https://vimeo.com/123456789
    // Player: https://player.vimeo.com/video/123456789
    // Private: https://vimeo.com/123456789/abc123hash
    // Private with params: https://vimeo.com/123456789/abc123hash?params
    const vimeoPrivatePattern = /vimeo\.com\/(\d+)\/([a-zA-Z0-9]+)/;
    const privateMatch = videoUrl.match(vimeoPrivatePattern);
    if (privateMatch) {
      return { platform: 'vimeo', id: privateMatch[1], hash: privateMatch[2] };
    }

    const vimeoPatterns = [
      /vimeo\.com\/(\d+)/,
      /player\.vimeo\.com\/video\/(\d+)/
    ];

    for (const pattern of vimeoPatterns) {
      const match = videoUrl.match(pattern);
      if (match) {
        // Check for hash parameter in player URL
        const hashMatch = videoUrl.match(/[?&]h=([a-zA-Z0-9]+)/);
        return { platform: 'vimeo', id: match[1], hash: hashMatch ? hashMatch[1] : null };
      }
    }

    // Bunny.net Stream patterns
    // Embed: https://player.mediadelivery.net/embed/{libraryId}/{videoId}
    // Play: https://player.mediadelivery.net/play/{libraryId}/{videoId}
    // Also support iframe.mediadelivery.net variant
    // User may paste full iframe embed code — extract src URL first
    let urlToCheck = videoUrl;
    const iframeSrcMatch = videoUrl.match(/src=["']([^"']+mediadelivery\.net[^"']+)["']/);
    if (iframeSrcMatch) {
      urlToCheck = iframeSrcMatch[1];
    }

    const bunnyEmbedPattern = /(?:player|iframe)\.mediadelivery\.net\/embed\/(\d+)\/([a-f0-9-]+)/;
    const bunnyEmbedMatch = urlToCheck.match(bunnyEmbedPattern);
    if (bunnyEmbedMatch) {
      return { platform: 'bunny', libraryId: bunnyEmbedMatch[1], id: bunnyEmbedMatch[2] };
    }

    const bunnyPlayPattern = /(?:player|iframe)\.mediadelivery\.net\/play\/(\d+)\/([a-f0-9-]+)/;
    const bunnyPlayMatch = urlToCheck.match(bunnyPlayPattern);
    if (bunnyPlayMatch) {
      return { platform: 'bunny', libraryId: bunnyPlayMatch[1], id: bunnyPlayMatch[2] };
    }

    const bunnyOldPattern = /video\.bunnycdn\.com\/play\/(\d+)\/([a-f0-9-]+)/;
    const bunnyOldMatch = videoUrl.match(bunnyOldPattern);
    if (bunnyOldMatch) {
      return { platform: 'bunny', libraryId: bunnyOldMatch[1], id: bunnyOldMatch[2] };
    }

    // Rumble patterns
    // Embed URL: https://rumble.com/embed/{embedId}/  (embedId may be dotted, e.g. u4nvf6q.v70bqqu)
    // Full iframe embed code — extract src first
    // Share URL: https://rumble.com/{videoId}-title.html (treated as share — embed via oEmbed at render is not reliable, so we accept it and let user paste embed URL/code instead)
    let rumbleUrlToCheck = videoUrl;
    const rumbleIframeSrcMatch = videoUrl.match(/src=["']([^"']+rumble\.com[^"']+)["']/);
    if (rumbleIframeSrcMatch) {
      rumbleUrlToCheck = rumbleIframeSrcMatch[1];
    }

    const rumbleEmbedPattern = /rumble\.com\/embed\/([a-zA-Z0-9._-]+)\/?/;
    const rumbleEmbedMatch = rumbleUrlToCheck.match(rumbleEmbedPattern);
    if (rumbleEmbedMatch) {
      return { platform: 'rumble', id: rumbleEmbedMatch[1] };
    }

    // Rumble share URL — accept but mark as share so embed renders share-fallback message
    const rumbleSharePattern = /rumble\.com\/(v[a-z0-9]+)(?:-[^/]*)?\.html/i;
    const rumbleShareMatch = videoUrl.match(rumbleSharePattern);
    if (rumbleShareMatch) {
      return { platform: 'rumble', id: rumbleShareMatch[1], isShareUrl: true };
    }

    // Check if it's just an ID (assume Vimeo for backward compatibility)
    if (/^\d+$/.test(videoUrl)) {
      return { platform: 'vimeo', id: videoUrl };
    }

    return null;
  };

  // Get YouTube thumbnail (no API key needed)
  const getYouTubeThumbnail = (videoId) => {
    // YouTube provides thumbnails at predictable URLs
    // maxresdefault.jpg is highest quality, fallback to hqdefault.jpg
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  };

  // Fetch Vimeo thumbnail via oEmbed API
  const getVimeoThumbnail = async (videoId) => {
    try {
      const response = await fetch(
        `https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoId}&width=1280`
      );
      if (!response.ok) return null;
      const data = await response.json();
      return data.thumbnail_url?.replace(/_\d+x\d+/, '_1280x720') || null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      setError(null);
      
      const parsed = parseVideoUrl(url);
      if (!parsed) {
        setError('Invalid video URL');
        setLoading(false);
        return;
      }

      // Rumble share URLs cannot be embedded — embed ID differs from share slug
      if (parsed.platform === 'rumble' && parsed.isShareUrl) {
        setVideoData(parsed);
        setError('Rumble share URL detected — please use the Embed URL (or paste the iframe code) from the "Embed" button under the video.');
        setLoading(false);
        return;
      }

      setVideoData(parsed);
      
      // Fetch thumbnail
      if (showThumbnail || onThumbnailLoad) {
        let thumbUrl = null;
        
        if (parsed.platform === 'youtube') {
          thumbUrl = getYouTubeThumbnail(parsed.id);
        } else if (parsed.platform === 'vimeo') {
          thumbUrl = await getVimeoThumbnail(parsed.id);
        }
        
        if (thumbUrl) {
          setThumbnail(thumbUrl);
          if (onThumbnailLoad) {
            onThumbnailLoad(thumbUrl);
          }
        }
      }
      
      setLoading(false);
    };

    init();
  }, [url, showThumbnail, onThumbnailLoad]);

  // Lock to landscape when video iframe goes fullscreen (fixes Android auto-rotate)
  useEffect(() => {
    const handleFullscreenChange = () => {
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
      if (fsEl && (fsEl.tagName === 'IFRAME' || fsEl.querySelector?.('iframe'))) {
        if (screen.orientation?.lock) {
          screen.orientation.lock('landscape').catch(() => {});
        }
      } else if (!fsEl) {
        if (screen.orientation?.unlock) {
          try { screen.orientation.unlock(); } catch (e) {}
        }
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Get embed URL based on platform
  // If user clicked the thumbnail play button, autoplay the embed so they don't have to click twice
  const getEmbedUrl = () => {
    if (!videoData) return null;
    const shouldAutoplay = autoplay || (showThumbnail && playing);
    
    if (videoData.platform === 'youtube') {
      return `https://www.youtube.com/embed/${videoData.id}?autoplay=${shouldAutoplay ? 1 : 0}&rel=0&modestbranding=1`;
    } else if (videoData.platform === 'vimeo') {
      const hashParam = videoData.hash ? `&h=${videoData.hash}` : '';
      return `https://player.vimeo.com/video/${videoData.id}?badge=0&autopause=0&player_id=0&app_id=58479&fullscreen=1${hashParam}${shouldAutoplay ? '&autoplay=1' : ''}`;
    } else if (videoData.platform === 'bunny') {
      return `https://player.mediadelivery.net/embed/${videoData.libraryId}/${videoData.id}?autoplay=${shouldAutoplay}&loop=false&muted=false&preload=true&responsive=true`;
    } else if (videoData.platform === 'rumble') {
      // Rumble does not support share-URL embeds — only embed IDs work
      if (videoData.isShareUrl) return null;
      return `https://rumble.com/embed/${videoData.id}/?pub=4${shouldAutoplay ? '&autoplay=2' : ''}`;
    }
    return null;
  };

  // Get platform display name
  const getPlatformLabel = () => {
    if (!videoData) return 'VIDEO';
    if (videoData.platform === 'youtube') return 'YOUTUBE';
    if (videoData.platform === 'bunny') return 'BUNNY STREAM';
    if (videoData.platform === 'rumble') return 'RUMBLE';
    return 'VIMEO';
  };

  if (loading) {
    return (
      <div className={`relative bg-gray-900 rounded-lg overflow-hidden ${className}`} style={{ aspectRatio: '16/9' }}>
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !videoData) {
    return (
      <div className={`relative bg-gray-900 rounded-lg overflow-hidden ${className}`} style={{ aspectRatio: '16/9' }}>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-red-500">
          <AlertCircle className="w-8 h-8 mb-2" />
          <span className="font-typewriter text-sm">{error || 'Invalid video'}</span>
        </div>
      </div>
    );
  }

  // Show thumbnail with play button
  if (showThumbnail && !playing) {
    return (
      <div 
        className={`relative bg-gray-900 rounded-lg overflow-hidden cursor-pointer group ${className}`} 
        style={{ aspectRatio: '16/9' }}
        onClick={() => setPlaying(true)}
        data-testid="video-thumbnail"
      >
        {thumbnail && (
          <img 
            src={thumbnail} 
            alt="Video thumbnail"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            style={{ filter: 'brightness(0.8)' }}
            onError={(e) => {
              // Fallback for YouTube if maxresdefault doesn't exist
              if (videoData.platform === 'youtube' && e.target.src.includes('maxresdefault')) {
                e.target.src = `https://img.youtube.com/vi/${videoData.id}/hqdefault.jpg`;
              }
            }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className={`w-20 h-20 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110 ${
            videoData.platform === 'youtube' 
              ? 'bg-red-600/90 group-hover:bg-red-500' 
              : videoData.platform === 'bunny'
              ? 'bg-orange-500/90 group-hover:bg-orange-400'
              : videoData.platform === 'rumble'
              ? 'bg-green-600/90 group-hover:bg-green-500'
              : 'bg-amber-600/90 group-hover:bg-amber-500'
          }`}>
            <Play className="w-10 h-10 text-white ml-1" fill="currentColor" />
          </div>
        </div>
        <div className="absolute bottom-4 left-4">
          <span className={`px-3 py-1 rounded font-headline tracking-wider text-sm ${
            videoData.platform === 'youtube'
              ? 'bg-red-600/90 text-white'
              : videoData.platform === 'bunny'
              ? 'bg-orange-500/90 text-white'
              : videoData.platform === 'rumble'
              ? 'bg-green-600/90 text-white'
              : 'bg-black/70 text-amber-500'
          }`}>
            {getPlatformLabel()} VIDEO
          </span>
        </div>
      </div>
    );
  }

  // Embedded player
  return (
    <div 
      className={`relative bg-black rounded-lg overflow-hidden ${className}`} 
      style={{ aspectRatio: '16/9' }}
      data-testid="video-player"
    >
      <iframe
        ref={iframeRef}
        src={getEmbedUrl()}
        className="absolute inset-0 w-full h-full"
        frameBorder="0"
        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
        title="Video player"
      />
    </div>
  );
};

/**
 * Utility function to parse video URL and get platform + ID + hash
 */
export const parseVideoUrl = (url) => {
  if (!url) return null;

  // YouTube
  const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  if (youtubeMatch) return { platform: 'youtube', id: youtubeMatch[1] };

  // Bunny.net Stream — handle full iframe embed code or direct URLs
  let urlToCheck = url;
  const iframeSrcMatch = url.match(/src=["']([^"']+mediadelivery\.net[^"']+)["']/);
  if (iframeSrcMatch) {
    urlToCheck = iframeSrcMatch[1];
  }

  const bunnyEmbedMatch = urlToCheck.match(/(?:player|iframe)\.mediadelivery\.net\/embed\/(\d+)\/([a-f0-9-]+)/);
  if (bunnyEmbedMatch) return { platform: 'bunny', libraryId: bunnyEmbedMatch[1], id: bunnyEmbedMatch[2] };

  const bunnyPlayMatch = urlToCheck.match(/(?:player|iframe)\.mediadelivery\.net\/play\/(\d+)\/([a-f0-9-]+)/);
  if (bunnyPlayMatch) return { platform: 'bunny', libraryId: bunnyPlayMatch[1], id: bunnyPlayMatch[2] };

  const bunnyOldMatch = url.match(/video\.bunnycdn\.com\/play\/(\d+)\/([a-f0-9-]+)/);
  if (bunnyOldMatch) return { platform: 'bunny', libraryId: bunnyOldMatch[1], id: bunnyOldMatch[2] };

  // Rumble — embed URL or full iframe embed code
  let rumbleUrlToCheck = url;
  const rumbleIframeSrcMatch = url.match(/src=["']([^"']+rumble\.com[^"']+)["']/);
  if (rumbleIframeSrcMatch) {
    rumbleUrlToCheck = rumbleIframeSrcMatch[1];
  }

  const rumbleEmbedMatch = rumbleUrlToCheck.match(/rumble\.com\/embed\/([a-zA-Z0-9._-]+)\/?/);
  if (rumbleEmbedMatch) return { platform: 'rumble', id: rumbleEmbedMatch[1] };

  // Rumble share URL — accepted as valid but flagged
  const rumbleShareMatch = url.match(/rumble\.com\/(v[a-z0-9]+)(?:-[^/]*)?\.html/i);
  if (rumbleShareMatch) return { platform: 'rumble', id: rumbleShareMatch[1], isShareUrl: true };

  // Vimeo private (with hash in path): https://vimeo.com/123456789/abc123hash
  const vimeoPrivateMatch = url.match(/vimeo\.com\/(\d+)\/([a-zA-Z0-9]+)/);
  if (vimeoPrivateMatch) return { platform: 'vimeo', id: vimeoPrivateMatch[1], hash: vimeoPrivateMatch[2] };

  // Vimeo standard or player URL
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    // Check for hash parameter
    const hashMatch = url.match(/[?&]h=([a-zA-Z0-9]+)/);
    return { platform: 'vimeo', id: vimeoMatch[1], hash: hashMatch ? hashMatch[1] : null };
  }

  // Player URL
  const playerMatch = url.match(/player\.vimeo\.com\/video\/(\d+)/);
  if (playerMatch) {
    const hashMatch = url.match(/[?&]h=([a-zA-Z0-9]+)/);
    return { platform: 'vimeo', id: playerMatch[1], hash: hashMatch ? hashMatch[1] : null };
  }

  // Just Vimeo ID
  if (/^\d+$/.test(url)) return { platform: 'vimeo', id: url, hash: null };

  return null;
};

/**
 * Validate if a string is a valid video URL (Vimeo, YouTube, or Bunny Stream)
 */
export const isValidVideoUrl = (url) => {
  return parseVideoUrl(url) !== null;
};

/**
 * Get video thumbnail URL
 */
export const getVideoThumbnail = async (url) => {
  const parsed = parseVideoUrl(url);
  if (!parsed) return null;
  
  if (parsed.platform === 'youtube') {
    return `https://img.youtube.com/vi/${parsed.id}/maxresdefault.jpg`;
  } else if (parsed.platform === 'vimeo') {
    try {
      const response = await fetch(
        `https://vimeo.com/api/oembed.json?url=https://vimeo.com/${parsed.id}&width=1280`
      );
      if (!response.ok) return null;
      const data = await response.json();
      return data.thumbnail_url?.replace(/_\d+x\d+/, '_1280x720') || null;
    } catch {
      return null;
    }
  }
  // Bunny.net thumbnails require API key — returns null, embed shows its own poster
  return null;
};

/**
 * Legacy exports for backward compatibility with VimeoPlayer
 */
export const extractVimeoId = (url) => {
  const parsed = parseVideoUrl(url);
  return parsed?.platform === 'vimeo' ? parsed.id : null;
};

export const isValidVimeoUrl = (url) => {
  const parsed = parseVideoUrl(url);
  return parsed?.platform === 'vimeo';
};

export const getVimeoThumbnail = async (url) => {
  const parsed = parseVideoUrl(url);
  if (parsed?.platform !== 'vimeo') return null;
  
  try {
    const response = await fetch(
      `https://vimeo.com/api/oembed.json?url=https://vimeo.com/${parsed.id}&width=1280`
    );
    if (!response.ok) return null;
    const data = await response.json();
    return data.thumbnail_url?.replace(/_\d+x\d+/, '_1280x720') || null;
  } catch {
    return null;
  }
};

export default VideoPlayer;
